/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — data + actions hook (Tranche 2).

   Owns: base lookups, the selected member/week's entries/forecasts/
   vacation, the approval queue, and every mutation. No JSX here —
   TimesheetsPage and its children render what this returns.

   D7 (Josh, locked decision): forecasts are NEVER auto-materialized
   into hour_entries on load. The old silent auto-seed is gone —
   `loadWeek` only reads. Every write below is a direct result of a
   user action (cell commit, ghost accept, status transition).
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { TimeService } from '../../services/timeService';
import { TeamService } from '../../services/teamService';
import { ProjectService } from '../../services/projectService';
import { AdminService } from '../../services/adminService';
import { VacationService } from '../../services/vacationService';
import { hasPermission, hasAnyPermission } from '../../../shared/permissions';
import type { AuthUser } from '../../services/authService';
import type {
  HourEntry,
  HourForecast,
  TeamMember,
  Project,
  ActivityCode,
  Timesheet,
  OverheadCategory,
  ProjectPhase,
  VacationCategory,
  FirmHoliday,
  VacationRequest,
} from '../../../shared/types';
import type { ProjectRow, TimeExpenseDeepLink } from './types';
import { mondayOf, addDays, mondayOfTodayISO, weekDatesFrom } from './dateUtils';
import { getForecastForCell } from './computeGrid';

function settled<T>(r: PromiseSettledResult<T>, fallback: T): T {
  return r.status === 'fulfilled' ? r.value : fallback;
}

export interface UseTimesheetDataArgs {
  authUser?: AuthUser;
  deepLink?: TimeExpenseDeepLink | null;
  onDeepLinkConsumed?: () => void;
  toast: (msg: string, isError?: boolean) => void;
}

export function useTimesheetData({
  authUser,
  deepLink,
  onDeepLinkConsumed,
  toast,
}: UseTimesheetDataArgs) {
  const [weekStart, setWeekStart] = useState<string>(mondayOfTodayISO());
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [overheadCategories, setOverheadCategories] = useState<OverheadCategory[]>([]);
  const [allPhases, setAllPhases] = useState<ProjectPhase[]>([]);
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [allTimesheets, setAllTimesheets] = useState<Timesheet[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [projectRows, setProjectRows] = useState<ProjectRow[]>([]);
  const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [firmHolidays, setFirmHolidays] = useState<FirmHoliday[]>([]);
  const [vacationCategories, setVacationCategories] = useState<VacationCategory[]>([]);
  const [queue, setQueue] = useState<Timesheet[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const rowKeyCounter = useRef(0);

  function makeKey() {
    return `row-${++rowKeyCounter.current}`;
  }

  const perms = authUser?.permissions;
  // "Approver" = can move statuses forward (Approve/Reject/Lock).
  const isApprover = hasPermission(perms, 'timesheets.approve');
  const canUnlock = hasPermission(perms, 'timesheets.unlock');
  // "Can view others" = the member switcher shows. Approve implies you
  // need to see the sheet you're approving, so it counts too.
  const canViewOthers = hasAnyPermission(perms, [
    'timesheets.view_all',
    'timesheets.view_team',
    'timesheets.approve',
  ]);

  const weekDates = weekDatesFrom(weekStart);
  const isLocked = timesheet?.status === 'locked';

  // ── Base data (once) ─────────────────────────────────────────
  const loadBase = useCallback(async () => {
    try {
      const [projsR, memsR, codesR, ohCatsR, vCatsR, fHolsR] = await Promise.allSettled([
        ProjectService.list(),
        TeamService.list(),
        AdminService.activityCodes.list(),
        AdminService.overheadCategories.list(),
        VacationService.categories.list(),
        VacationService.holidays.list(),
      ]);
      const projs = settled(projsR, [] as Project[]);
      const mems = settled(memsR, [] as TeamMember[]);
      const codes = settled(codesR, [] as ActivityCode[]);
      const ohCats = settled(ohCatsR, [] as OverheadCategory[]);
      const vCats = settled(vCatsR, [] as VacationCategory[]);
      const fHols = settled(fHolsR, [] as FirmHoliday[]);
      const activeProjs = projs.filter((p) => p.status === 'active');
      const activeMembers = mems.filter((m) => m.active);
      setProjects(activeProjs);
      setMembers(activeMembers);
      setActivityCodes(codes);
      setOverheadCategories(ohCats);
      setVacationCategories(vCats);
      setFirmHolidays(fHols);

      // Resolve the member FIRST — the grid renders on it; phases are
      // display detail and must never gate first paint (perf floor).
      const myId = authUser?.teamMemberId || authUser?.id;
      const me = myId ? activeMembers.find((m) => m.id === myId) : null;
      setSelectedMemberId((prev) => prev || me?.id || activeMembers[0]?.id || '');

      try {
        // One bulk fetch — the per-project loop was 400+ sequential calls.
        setAllPhases(await TimeService.listAllPhases());
      } catch {
        /* phases failing shouldn't blank the page */
      }
    } catch (err: any) {
      setError('Failed to load data: ' + (err.message || 'Unknown error'));
    }
  }, [authUser]);

  useEffect(() => {
    loadBase();
  }, [loadBase]);
  useEffect(() => {
    const handler = () => loadBase();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [loadBase]);

  // ── Deep-link consumer (dashboard widgets → tab/project/week/member) ──
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.weekStart && /^\d{4}-\d{2}-\d{2}$/.test(deepLink.weekStart)) {
      setWeekStart(mondayOf(deepLink.weekStart));
    }
    if (deepLink.memberId) setSelectedMemberId(deepLink.memberId);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  // ── Week data (read-only — no auto-seed, D7) ─────────────────
  const loadWeek = useCallback(async () => {
    if (!selectedMemberId) return;
    setLoadingWeek(true);
    try {
      const weekEndStr = addDays(weekStart, 6);
      const [tsR, entriesR, historyR, vReqsR, fcsR] = await Promise.allSettled([
        TimeService.getTimesheetByWeek(selectedMemberId, weekStart),
        TimeService.listEntries(undefined, weekStart, weekEndStr, selectedMemberId),
        TimeService.listTimesheets(selectedMemberId),
        VacationService.listRequestsInRange(weekStart, weekEndStr, selectedMemberId),
        TimeService.listForecastsForRange(weekStart, weekEndStr, [selectedMemberId]).catch(
          () => []
        ),
      ]);
      const ts = settled(tsR, null as Timesheet | null);
      const allEntries = settled(entriesR, [] as HourEntry[]);
      const history = settled(historyR, [] as Timesheet[]);
      const vReqs = settled(vReqsR, [] as VacationRequest[]);
      const fcs = settled(fcsR, [] as HourForecast[]);

      setTimesheet(ts);
      setEntries(allEntries);
      setAllTimesheets(history.sort((a, b) => b.weekStart.localeCompare(a.weekStart)));
      setVacationRequests(vReqs);
      setForecasts(fcs);

      // Rebuild project rows — real entries first, then any forecasted
      // project not already represented (so the ghost has somewhere to
      // render). Never a write; purely a display scaffold.
      const seenKeys = new Map<string, ProjectRow>();
      const projectEntries = allEntries.filter((e) => e.projectId && !e.overheadCategory);
      for (const e of projectEntries) {
        const rowKey = `${e.projectId}|${e.activityCodeId || ''}`;
        if (!seenKeys.has(rowKey)) {
          seenKeys.set(rowKey, {
            key: makeKey(),
            projectId: e.projectId,
            activityCodeId: e.activityCodeId || '',
          });
        }
      }
      for (const f of fcs) {
        if (f.sourceKind === 'vacation' || !f.projectId) continue;
        const rowKey = `${f.projectId}|`;
        if (!seenKeys.has(rowKey) && !projectEntries.some((e) => e.projectId === f.projectId)) {
          seenKeys.set(rowKey, { key: makeKey(), projectId: f.projectId, activityCodeId: '' });
        }
      }
      setProjectRows(
        seenKeys.size > 0
          ? Array.from(seenKeys.values())
          : [{ key: makeKey(), projectId: '', activityCodeId: '' }]
      );
    } catch (err: any) {
      console.error('loadWeek error:', err);
      toast('Error loading timesheet: ' + err.message, true);
    } finally {
      setLoadingWeek(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMemberId, weekStart]);

  useEffect(() => {
    if (selectedMemberId) loadWeek();
  }, [loadWeek, selectedMemberId]);

  // ── Approval queue (approvers only) ──────────────────────────
  const refreshQueue = useCallback(async () => {
    if (!isApprover) {
      setQueue([]);
      return;
    }
    try {
      const submitted = await TimeService.listTimesheets(undefined, 'submitted');
      setQueue(submitted.sort((a, b) => (a.submittedAt || '').localeCompare(b.submittedAt || '')));
    } catch (err: any) {
      toast('Error loading approval queue: ' + err.message, true);
    }
  }, [isApprover, toast]);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);
  useEffect(() => {
    const handler = () => refreshQueue();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, [refreshQueue]);

  // Ensure a timesheet exists before a write — created on first cell
  // commit / accept, never speculatively on page load.
  async function ensureTimesheet(): Promise<Timesheet> {
    if (timesheet) return timesheet;
    if (!selectedMemberId) throw new Error('No team member selected');
    const ts = await TimeService.getOrCreateTimesheet(selectedMemberId, weekStart);
    setTimesheet(ts);
    setAllTimesheets((prev) =>
      prev.some((t) => t.id === ts.id)
        ? prev
        : [ts, ...prev].sort((a, b) => b.weekStart.localeCompare(a.weekStart))
    );
    return ts;
  }

  // ── Cell writes — optimistic single-cell upsert, no full reload ──
  const saveProjectHours = useCallback(
    async (row: ProjectRow, dateISO: string, hours: number) => {
      if (isLocked) {
        toast('This timesheet is locked.', true);
        return;
      }
      const existing = entries.find(
        (e) =>
          e.projectId === row.projectId &&
          (e.activityCodeId || '') === row.activityCodeId &&
          e.date === dateISO &&
          !e.overheadCategory
      );
      if (hours === existing?.hours || (hours === 0 && !existing)) return; // no-op
      const rollback = entries;
      const optimisticId = existing?.id || `optimistic-${crypto.randomUUID()}`;
      if (hours === 0 && existing) {
        setEntries((es) => es.filter((e) => e.id !== existing.id));
      } else if (hours > 0) {
        const optimistic: HourEntry = {
          id: optimisticId,
          projectId: row.projectId,
          teamMemberId: selectedMemberId,
          date: dateISO,
          hours,
          billable: true,
          activityCodeId: row.activityCodeId || undefined,
          timesheetId: timesheet?.id,
          overheadCategory: undefined,
          createdAt: existing?.createdAt || '',
          updatedAt: '',
        };
        setEntries((es) =>
          existing ? es.map((e) => (e.id === existing.id ? optimistic : e)) : [...es, optimistic]
        );
      }
      try {
        if (hours === 0 && existing) {
          await TimeService.deleteEntry(existing.id);
        } else if (hours > 0) {
          const ts = await ensureTimesheet();
          const saved = await TimeService.saveEntry({
            id: existing?.id || '',
            projectId: row.projectId,
            teamMemberId: selectedMemberId,
            date: dateISO,
            hours,
            billable: true,
            activityCodeId: row.activityCodeId || undefined,
            timesheetId: ts.id,
            overheadCategory: undefined,
            createdAt: '',
            updatedAt: '',
          } as HourEntry);
          setEntries((es) => es.map((e) => (e.id === optimisticId ? saved : e)));
        }
      } catch (err: any) {
        setEntries(rollback);
        toast('Error saving hours: ' + err.message, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, isLocked, selectedMemberId, timesheet]
  );

  const saveOverheadHours = useCallback(
    async (category: string, dateISO: string, hours: number) => {
      if (isLocked) {
        toast('This timesheet is locked.', true);
        return;
      }
      const existing = entries.find((e) => e.overheadCategory === category && e.date === dateISO);
      if (hours === existing?.hours || (hours === 0 && !existing)) return;
      const rollback = entries;
      const optimisticId = existing?.id || `optimistic-${crypto.randomUUID()}`;
      if (hours === 0 && existing) {
        setEntries((es) => es.filter((e) => e.id !== existing.id));
      } else if (hours > 0) {
        const optimistic: HourEntry = {
          id: optimisticId,
          projectId: '',
          teamMemberId: selectedMemberId,
          date: dateISO,
          hours,
          billable: false,
          overheadCategory: category,
          timesheetId: timesheet?.id,
          createdAt: existing?.createdAt || '',
          updatedAt: '',
        };
        setEntries((es) =>
          existing ? es.map((e) => (e.id === existing.id ? optimistic : e)) : [...es, optimistic]
        );
      }
      try {
        if (hours === 0 && existing) {
          await TimeService.deleteEntry(existing.id);
        } else if (hours > 0) {
          const ts = await ensureTimesheet();
          const saved = await TimeService.saveEntry({
            id: existing?.id || '',
            projectId: '',
            teamMemberId: selectedMemberId,
            date: dateISO,
            hours,
            billable: false,
            overheadCategory: category,
            timesheetId: ts.id,
            createdAt: '',
            updatedAt: '',
          } as HourEntry);
          setEntries((es) => es.map((e) => (e.id === optimisticId ? saved : e)));
        }
      } catch (err: any) {
        setEntries(rollback);
        toast('Error saving hours: ' + err.message, true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, isLocked, selectedMemberId, timesheet]
  );

  // ── Forecast ghosts — explicit accept only (D7) ──────────────
  const acceptGhost = useCallback(
    async (row: ProjectRow, dateISO: string) => {
      const fc = getForecastForCell(forecasts, row.projectId, dateISO);
      if (!fc || fc.hours <= 0) return;
      await saveProjectHours(row, dateISO, fc.hours);
    },
    [forecasts, saveProjectHours]
  );

  const acceptRow = useCallback(
    async (row: ProjectRow) => {
      const targets = weekDates.filter((ds) => {
        const hrs = entries.find(
          (e) =>
            e.projectId === row.projectId &&
            (e.activityCodeId || '') === row.activityCodeId &&
            e.date === ds &&
            !e.overheadCategory
        )?.hours;
        const fc = getForecastForCell(forecasts, row.projectId, ds);
        return (!hrs || hrs === 0) && fc && fc.hours > 0;
      });
      await Promise.allSettled(targets.map((ds) => acceptGhost(row, ds)));
    },
    [weekDates, entries, forecasts, acceptGhost]
  );

  const acceptWeek = useCallback(async () => {
    await Promise.allSettled(projectRows.map((row) => acceptRow(row)));
  }, [projectRows, acceptRow]);

  // ── Row management (UI scaffold only, no write) ──────────────
  function addProjectRow() {
    setProjectRows((prev) => [...prev, { key: makeKey(), projectId: '', activityCodeId: '' }]);
  }
  function removeProjectRow(key: string) {
    setProjectRows((prev) => prev.filter((r) => r.key !== key));
  }
  function updateProjectRow(key: string, patch: Partial<ProjectRow>) {
    setProjectRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  /** Deep-link support (dashboard "project" param): if the target
   *  project has no row yet, add one so it's visible immediately —
   *  UI scaffold only, never a write. */
  function ensureProjectRowForProject(projectId: string) {
    setProjectRows((prev) =>
      prev.some((r) => r.projectId === projectId)
        ? prev
        : [...prev, { key: makeKey(), projectId, activityCodeId: '' }]
    );
  }

  // ── Status transitions ────────────────────────────────────────
  function patchHistory(updated: Timesheet) {
    setAllTimesheets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    if (timesheet?.id === updated.id) setTimesheet(updated);
  }

  function canSelfUnsubmit(ts: Timesheet | null): boolean {
    if (!ts || ts.status !== 'submitted' || !ts.submittedAt) return false;
    const elapsed = Date.now() - new Date(ts.submittedAt).getTime();
    return elapsed < 10 * 60 * 1000; // 10 minutes
  }

  async function handleSubmit() {
    if (!timesheet || timesheet.status !== 'draft') return;
    try {
      const u = await TimeService.updateTimesheetStatus(
        timesheet.id,
        'submitted',
        selectedMemberId
      );
      patchHistory(u);
      refreshQueue();
      toast('Timesheet submitted.');
    } catch (err: any) {
      toast('Submit failed: ' + err.message, true);
    }
  }

  /** Owner (within 10 min) or an approver sends a submitted sheet back
   *  to draft — no reason recorded. Distinct from Reject (below). */
  async function handleUnsubmit() {
    if (!timesheet) return;
    if (!isApprover && !canSelfUnsubmit(timesheet)) {
      toast('The 10-minute un-submit window has passed. Contact an admin to un-submit.', true);
      return;
    }
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'draft', selectedMemberId);
      patchHistory(u);
      refreshQueue();
      toast('Timesheet returned to draft.');
    } catch (err: any) {
      toast('Unsubmit failed: ' + err.message, true);
    }
  }

  /** Approver sends an already-approved sheet back to draft. Same
   *  transition as Unsubmit under the hood but distinct copy — an
   *  approver un-approving is a different act from an owner pulling
   *  their own submission back. */
  async function handleUnapprove() {
    if (!timesheet || !isApprover || timesheet.status !== 'approved') return;
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'draft', selectedMemberId);
      patchHistory(u);
      toast('Approval reversed — timesheet returned to draft.');
    } catch (err: any) {
      toast('Unapprove failed: ' + err.message, true);
    }
  }

  async function handleApprove(id?: string) {
    const tsId = id || timesheet?.id;
    if (!tsId || !isApprover) return;
    try {
      const u = await TimeService.updateTimesheetStatus(tsId, 'approved', selectedMemberId);
      patchHistory(u);
      setQueue((q) => q.filter((t) => t.id !== tsId));
      toast('Timesheet approved.');
    } catch (err: any) {
      toast('Approve failed: ' + err.message, true);
    }
  }

  async function handleReject(reason: string, id?: string) {
    const tsId = id || timesheet?.id;
    if (!tsId || !isApprover) return;
    try {
      const u = await TimeService.rejectTimesheet(tsId, reason);
      patchHistory(u);
      setQueue((q) => q.filter((t) => t.id !== tsId));
      toast('Timesheet rejected — returned to draft.');
    } catch (err: any) {
      toast('Reject failed: ' + err.message, true);
    }
  }

  async function handleLock() {
    if (!timesheet || !isApprover || timesheet.status !== 'approved') return;
    try {
      const u = await TimeService.updateTimesheetStatus(timesheet.id, 'locked', selectedMemberId);
      patchHistory(u);
      toast('Timesheet locked.');
    } catch (err: any) {
      toast('Lock failed: ' + err.message, true);
    }
  }

  async function handleUnlock() {
    if (!timesheet || !canUnlock || timesheet.status !== 'locked') return;
    try {
      const u = await TimeService.unlockTimesheet(timesheet.id);
      patchHistory(u);
      toast('Timesheet unlocked — back to approved.');
    } catch (err: any) {
      toast('Unlock failed: ' + err.message, true);
    }
  }

  async function deleteTimesheet(tsId: string) {
    try {
      await TimeService.deleteTimesheet(tsId);
      setAllTimesheets((prev) => prev.filter((t) => t.id !== tsId));
      if (timesheet?.id === tsId) {
        await loadWeek();
      }
      toast('Timesheet deleted.');
    } catch (err: any) {
      toast('Delete failed: ' + err.message, true);
    }
  }

  async function createTimesheetForDate(dateISO: string) {
    if (!selectedMemberId) {
      toast('Still loading — please wait.', true);
      return;
    }
    const monday = mondayOf(dateISO);
    const exists = allTimesheets.find((t) => t.weekStart === monday);
    if (exists) {
      setWeekStart(monday);
      toast('Timesheet already exists for this week.');
      return;
    }
    try {
      const ts = await TimeService.getOrCreateTimesheet(selectedMemberId, monday);
      setWeekStart(monday);
      setAllTimesheets((prev) =>
        [ts, ...prev].sort((a, b) => b.weekStart.localeCompare(a.weekStart))
      );
      toast('Timesheet created.');
    } catch (err: any) {
      toast('Failed: ' + err.message, true);
    }
  }

  return {
    // state
    weekStart,
    setWeekStart,
    weekDates,
    entries,
    projects,
    members,
    activityCodes,
    overheadCategories,
    allPhases,
    timesheet,
    allTimesheets,
    selectedMemberId,
    setSelectedMemberId,
    projectRows,
    vacationRequests,
    forecasts,
    firmHolidays,
    vacationCategories,
    queue,
    error,
    loadingWeek,
    isApprover,
    canUnlock,
    canViewOthers,
    isLocked,
    // actions
    saveProjectHours,
    saveOverheadHours,
    acceptGhost,
    acceptRow,
    acceptWeek,
    addProjectRow,
    removeProjectRow,
    updateProjectRow,
    ensureProjectRowForProject,
    handleSubmit,
    handleUnsubmit,
    handleUnapprove,
    handleApprove,
    handleReject,
    handleLock,
    handleUnlock,
    deleteTimesheet,
    createTimesheetForDate,
    canSelfUnsubmit,
    refreshQueue,
  };
}

export type TimesheetData = ReturnType<typeof useTimesheetData>;
